import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiRadio, FiMusic, FiGlobe, FiSmartphone, FiHeadphones, FiHeart, FiCheck } from 'react-icons/fi';
import { IoMusicalNotes, IoCarSport } from 'react-icons/io5';
import Icon from '../Icon/Icon';

/* ============================================
   SECTION CONTAINERS
   ============================================ */

const SectionsWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

/* ---------- Feature Cards Section ---------- */

const FeaturesSection = styled.section`
  padding: 80px 20px;
  background: ${({ theme }) => theme.colors.background};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 60px 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 48px 12px;
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.15rem;
  color: ${({ theme }) => theme.colors.textLight};
  text-align: center;
  max-width: 650px;
  margin: 0 auto 48px;
  line-height: 1.7;
  padding: 0 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    margin-bottom: 28px;
    line-height: 1.6;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(38, 108, 127, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 32px 24px;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  cursor: default;
  will-change: transform;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(38, 108, 127, 0.12);
    border-color: rgba(255, 143, 49, 0.3);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 20px;
  }
`;

const FeatureIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, rgba(38, 108, 127, 0.7));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  box-shadow: 0 4px 16px rgba(38, 108, 127, 0.25);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 56px;
    height: 56px;
  }
`;

const FeatureTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
`;

const FeatureDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textLight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

/* ---------- Showcase / Image + Text Sections ---------- */

const ShowcaseSection = styled.section<{ bgColor?: string }>`
  padding: 80px 20px;
  background: ${({ bgColor }) => bgColor || 'transparent'};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 60px 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 40px 12px;
  }
`;

const ShowcaseRow = styled.div<{ reverse?: boolean }>`
  display: flex;
  align-items: center;
  gap: 60px;
  max-width: 1200px;
  margin: 0 auto;
  flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: 32px;
    text-align: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 24px;
  }
`;

const ShowcaseImageWrapper = styled(motion.div)`
  flex: 1;
  max-width: 480px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 380px;
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 100%;
    padding: 0 8px;
  }
`;

const ShowcaseImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ShowcaseContent = styled(motion.div)`
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 4px;
  }
`;

const ShowcaseTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 16px;
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.8rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.4rem;
  }
`;

const ShowcaseAccent = styled.span`
  color: ${({ theme }) => theme.colors.accent};
`;

const ShowcaseText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 18px;
  }
`;

const ShowcaseBullet = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    text-align: left;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const BulletIcon = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  margin-top: 2px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    font-size: 12px;
  }
`;

const BulletText = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

/* ============================================
   ANIMATION VARIANTS
   ============================================ */

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: 'easeOut' as const }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: 'easeOut' as const }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: 'easeOut' as const }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

/* ============================================
   COMPONENT
   ============================================ */

const HomeContent: React.FC = () => {
  return (
    <SectionsWrapper>
      {/* ---- WHY RADYOFONE ---- */}
      <FeaturesSection aria-label="Why Choose Radyofone">
        <SectionContainer>
          <SectionTitle
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            Why Choose Radyofone?
          </SectionTitle>
          <SectionSubtitle
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            Radyofone is more than just a radio app - it's your gateway to a world of music, entertainment, and live audio content, completely free.
          </SectionSubtitle>

          <FeaturesGrid
            as={motion.div}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <FeatureCard variants={fadeInUp}>
              <FeatureIconWrapper>
                <Icon icon={FiRadio} size={28} />
              </FeatureIconWrapper>
              <FeatureTitle>65+ Live Stations</FeatureTitle>
              <FeatureDescription>
                Stream over 65 live radio stations from around the world - Arabic, French, International, and more. New stations added regularly.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard variants={fadeInUp}>
              <FeatureIconWrapper>
                <Icon icon={FiMusic} size={28} />
              </FeatureIconWrapper>
              <FeatureTitle>Fresh Hits Daily</FeatureTitle>
              <FeatureDescription>
                Discover the latest Arabic and international hits. From Haifa Wehbe to Amr Diab, enjoy trending music the moment it drops.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard variants={fadeInUp}>
              <FeatureIconWrapper>
                <Icon icon={FiGlobe} size={28} />
              </FeatureIconWrapper>
              <FeatureTitle>10+ Categories</FeatureTitle>
              <FeatureDescription>
                Browse by category - Arabic Songs, French Songs, Lobnaniyat FM, Soul Notes, DJ Night, Kids Songs, Workout Motivation, and more.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard variants={fadeInUp}>
              <FeatureIconWrapper>
                <Icon icon={FiSmartphone} size={28} />
              </FeatureIconWrapper>
              <FeatureTitle>Every Device</FeatureTitle>
              <FeatureDescription>
                Available on iPhone, iPad, Android phones, tablets, Apple CarPlay, and Android Auto. Your music follows you everywhere.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard variants={fadeInUp}>
              <FeatureIconWrapper>
                <Icon icon={FiHeadphones} size={28} />
              </FeatureIconWrapper>
              <FeatureTitle>High-Quality Audio</FeatureTitle>
              <FeatureDescription>
                Crystal-clear streaming quality with smooth playback. No interruptions, no buffering - just pure listening pleasure.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard variants={fadeInUp}>
              <FeatureIconWrapper>
                <Icon icon={FiHeart} size={28} />
              </FeatureIconWrapper>
              <FeatureTitle>100% Free</FeatureTitle>
              <FeatureDescription>
                No subscriptions, no hidden fees. Radyofone is completely free to download and use. Just open, browse, and listen.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </SectionContainer>
      </FeaturesSection>

      {/* ---- YOUR MUSIC, YOUR VIBE ---- */}
      <ShowcaseSection bgColor="linear-gradient(180deg, #f5f0e8 0%, #fffbf3 100%)" aria-label="Your Music Your Vibe">
        <ShowcaseRow>
          <ShowcaseImageWrapper
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseImage
              src="/promo-app-browse.png"
              alt="Radyofone app showing radio station categories including Arabic Songs, French Songs, Lobnaniyat FM, DJ Night, and more"
              loading="lazy"
            />
          </ShowcaseImageWrapper>
          <ShowcaseContent
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseTitle>
              Your <ShowcaseAccent>Music</ShowcaseAccent>. Your Vibe. Totally Free.
            </ShowcaseTitle>
            <ShowcaseText>
              One tap and Radyofone takes you there. Browse through curated radio stations, discover trending tracks, and build your personal listening experience - all without spending a penny.
            </ShowcaseText>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={FiCheck} size={14} /></BulletIcon>
              <BulletText>Browse 10+ categories - Arabic Classics, French Hits, Workout Beats, and more</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={FiCheck} size={14} /></BulletIcon>
              <BulletText>See what's "Coming Up Next" with real-time track previews</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={FiCheck} size={14} /></BulletIcon>
              <BulletText>Save your favorite stations to your personal library</BulletText>
            </ShowcaseBullet>
          </ShowcaseContent>
        </ShowcaseRow>
      </ShowcaseSection>

      {/* ---- NEW HITS & LIVE STREAMING ---- */}
      <ShowcaseSection aria-label="Live Streaming and New Hits">
        <ShowcaseRow reverse>
          <ShowcaseImageWrapper
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseImage
              src="/promo-newhits.png"
              alt="Radyofone live streaming player showing Haifa Wehbe's Badna Nrou2 on Fresh Hits station with Coming Up Next feature"
              loading="lazy"
            />
          </ShowcaseImageWrapper>
          <ShowcaseContent
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseTitle>
              New Hits? You'll Hear Them Only on <ShowcaseAccent>Radyofone</ShowcaseAccent>
            </ShowcaseTitle>
            <ShowcaseText>
              Stay ahead of the music scene. Radyofone delivers the freshest Arabic and international hits straight to your ears with live radio streaming. From trending chart-toppers to underground gems.
            </ShowcaseText>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={IoMusicalNotes} size={14} /></BulletIcon>
              <BulletText>Live streaming with real-time "Now Playing" updates</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={IoMusicalNotes} size={14} /></BulletIcon>
              <BulletText>Curated playlists featuring top Middle Eastern artists</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={IoMusicalNotes} size={14} /></BulletIcon>
              <BulletText>Popular artists like Wael Kfoury, Elissa, Assi Al Hellani, and more</BulletText>
            </ShowcaseBullet>
          </ShowcaseContent>
        </ShowcaseRow>
      </ShowcaseSection>

      {/* ---- MULTI-DEVICE ---- */}
      <ShowcaseSection bgColor="linear-gradient(180deg, #f5f0e8 0%, #fffbf3 100%)" aria-label="Available on Every Device">
        <ShowcaseRow>
          <ShowcaseImageWrapper
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseImage
              src="/promo-multidevice.png"
              alt="Radyofone app running on iPhone, iPad, and laptop showing Browse by Category, Discover Something New, and Popular Artists screens"
              loading="lazy"
            />
          </ShowcaseImageWrapper>
          <ShowcaseContent
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseTitle>
              Your World. <ShowcaseAccent>Every Device</ShowcaseAccent>. Every Moment.
            </ShowcaseTitle>
            <ShowcaseText>
              Whether you're on your phone, tablet, or laptop - Radyofone gives you the same premium experience everywhere. Seamlessly switch between devices without missing a beat.
            </ShowcaseText>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={FiCheck} size={14} /></BulletIcon>
              <BulletText>iPhone & iPad - Optimized for all iOS devices</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={FiCheck} size={14} /></BulletIcon>
              <BulletText>Android phones & tablets - Full feature support</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={FiCheck} size={14} /></BulletIcon>
              <BulletText>Discover popular artists, browse stations, and explore new releases</BulletText>
            </ShowcaseBullet>
          </ShowcaseContent>
        </ShowcaseRow>
      </ShowcaseSection>

      {/* ---- CARPLAY & ANDROID AUTO ---- */}
      <ShowcaseSection aria-label="Apple CarPlay and Android Auto Support">
        <ShowcaseRow reverse>
          <ShowcaseImageWrapper
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseImage
              src="/promo-carplay.png"
              alt="Radyofone app on car dashboard through Apple CarPlay showing Amr Diab live streaming with Android Auto and Apple CarPlay support"
              loading="lazy"
            />
          </ShowcaseImageWrapper>
          <ShowcaseContent
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ShowcaseTitle>
              Drive Loud. <ShowcaseAccent>Drive Smart</ShowcaseAccent>.
            </ShowcaseTitle>
            <ShowcaseText>
              Take Radyofone on the road with Apple CarPlay and Android Auto support. Stream your favorite stations hands-free while driving - safe, smooth, and loud. Perfect for road trips and daily commutes.
            </ShowcaseText>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={IoCarSport} size={14} /></BulletIcon>
              <BulletText>Full Apple CarPlay integration - control from your car's screen</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={IoCarSport} size={14} /></BulletIcon>
              <BulletText>Android Auto support for safe, hands-free listening</BulletText>
            </ShowcaseBullet>
            <ShowcaseBullet>
              <BulletIcon><Icon icon={IoCarSport} size={14} /></BulletIcon>
              <BulletText>Live streaming with artist info displayed on your dashboard</BulletText>
            </ShowcaseBullet>
          </ShowcaseContent>
        </ShowcaseRow>
      </ShowcaseSection>

    </SectionsWrapper>
  );
};

export default HomeContent;
